"use client";

import { useEffect, useState } from "react";

import { brand } from "shared-lib/helpers/envFunctions";
import { logInfo } from "shared-lib/helpers/logging/logger";

import RestrictedComponent from "@/components/RestrictedComponent";
import { GlobalActionTypes, useGlobalState } from "@/context/globalState";

interface GeoData {
  country: string;
  state?: string;
  stateName?: string;
}
const isSkippingGeoGuard =
  brand.id !== "cookingprofessionally" || process.env.NEXT_PUBLIC_E2E === "true";

export default function GeoGuard({ children }: { children: React.ReactNode }) {
  const [allowed, setAllowed] = useState<boolean>(true);
  const [, dispatch] = useGlobalState();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("forceRestricted") === "true") {
      logInfo("[GeoGuard] >>> forceRestricted param detected → forcing restricted view");
      setAllowed(false);
      dispatch({
        type: GlobalActionTypes.SET_WEB_APP_ENABLED,
        payload: false,
      });
      return;
    }

    if (isSkippingGeoGuard) {
      logInfo(
        `[GeoGuard] >>> Skipping geo-check (brand=${brand.id}, E2E=${process.env.NEXT_PUBLIC_E2E}).`
      );
      return;
    }

    logInfo("[GeoGuard] >>> Initializing geolocation check…");

    const updateAccess = (geo: GeoData) => {
      const isUS = geo.country?.toLowerCase() === "us";
      logInfo("[GeoGuard] >>> Geo data received:", geo, "=> allowed=", isUS);
      setAllowed(isUS);
      dispatch({
        type: GlobalActionTypes.SET_WEB_APP_ENABLED,
        payload: isUS,
      });
      logInfo(`[GeoGuard] >>> Dispatched SET_WEB_APP_ENABLED=${isUS}`);
    };

    if (window.OneTrust?.getGeolocationData) {
      logInfo("[GeoGuard] >>> OneTrust available, fetching geo data…");
      updateAccess(window.OneTrust.getGeolocationData());
    } else {
      logInfo("[GeoGuard] >>> OneTrust not ready, polling every 500ms…");
      const interval = setInterval(() => {
        if (window.OneTrust?.getGeolocationData) {
          logInfo("[GeoGuard] >>> OneTrust now ready, fetching geo data…");
          updateAccess(window.OneTrust.getGeolocationData());
          clearInterval(interval);
          logInfo("[GeoGuard] >>> Cleared polling interval");
        }
      }, 500);

      return () => {
        clearInterval(interval);
        logInfo("[GeoGuard] >>> Cleanup: Cleared polling interval");
      };
    }
  }, [dispatch]);

  if (!allowed) {
    logInfo("[GeoGuard] >>> Allowed is false, rendering RestrictedComponent");
    return <RestrictedComponent />;
  }

  logInfo("[GeoGuard] >>> Allowed is true, rendering children");
  return <>{children}</>;
}
